// src/pages/Home/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import { services, companyInfo, forkliftTypes } from '../../constants/data';

const Home = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Image URLs for carousel
  const carouselImages = [
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/1.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/2.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/3.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/4.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/5.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/6.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/7.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/8.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/9.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/10.jpg",
    "https://aaraventerpriseimages.blob.core.windows.net/website/carouselImages/11.jpg"
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const handlePrev = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setActiveIndex((prevIndex) => 
      (prevIndex + 1) % carouselImages.length
    );
  };

  const handleIndicatorClick = (index) => {
    setActiveIndex(index);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section bg-primary text-white py-5">
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="display-4 fw-bold mb-4">Premium Forklift Solutions for Your Business</h1>
              <p className="lead mb-4">Reliable forklift rentals, genuine spare parts, and expert maintenance services to keep your operations running smoothly.</p>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/contact" className="btn btn-light btn-lg px-4">
                  <i className="bi bi-calendar-check me-2"></i>Book Service Now
                </Link>
                <Link to="/services" className="btn btn-outline-light btn-lg px-4">
                  Explore Services <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="bg-white rounded p-4 shadow-lg">
                <h3 className="text-dark mb-3">Available Forklift Types</h3>
                <div className="row g-3">
                  {forkliftTypes.map((type, index) => (
                    <div key={index} className="col-md-6">
                      <div className="d-flex align-items-center p-3 bg-light rounded">
                        <i className="bi bi-check-circle-fill text-primary me-3"></i>
                        <span className="text-dark">{type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Carousel Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="display-5 fw-bold mb-3">Our Work & Equipment Gallery</h2>
            <p className="text-muted lead">Explore our fleet of modern forklifts and industrial solutions</p>
          </div>
          
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div id="galleryCarousel" className="carousel slide" data-bs-ride="carousel">
                {/* Carousel Indicators */}
                <div className="carousel-indicators">
                  {carouselImages.slice(0, 5).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      data-bs-target="#galleryCarousel"
                      data-bs-slide-to={index}
                      className={index === 0 ? 'active' : ''}
                      aria-current={index === 0 ? 'true' : 'false'}
                      aria-label={`Slide ${index + 1}`}
                      onClick={() => handleIndicatorClick(index)}
                    ></button>
                  ))}
                </div>

                {/* Carousel Items */}
                <div className="carousel-inner rounded shadow-lg">
                  {carouselImages.map((image, index) => (
                    <div 
                      key={index} 
                      className={`carousel-item ${index === activeIndex ? 'active' : ''}`}
                      style={{ 
                        height: '500px',
                        overflow: 'hidden'
                      }}
                    >
                      <img 
                        src={image} 
                        className="d-block w-100 h-100 object-fit-cover" 
                        alt={`Forklift Service Gallery ${index + 1}`}
                        loading="lazy"
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-50 rounded p-3">
                        <h5>Professional Forklift Solutions</h5>
                        <p>Quality equipment for all industrial needs</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Carousel Controls */}
                <button 
                  className="carousel-control-prev" 
                  type="button" 
                  onClick={handlePrev}
                >
                  <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                  <span className="visually-hidden">Previous</span>
                </button>
                <button 
                  className="carousel-control-next" 
                  type="button" 
                  onClick={handleNext}
                >
                  <span className="carousel-control-next-icon" aria-hidden="true"></span>
                  <span className="visually-hidden">Next</span>
                </button>
              </div>

              {/* Thumbnail Gallery */}
              <div className="row g-2 mt-3">
                {carouselImages.slice(0, 6).map((image, index) => (
                  <div key={index} className="col-4 col-md-2">
                    <button
                      className="thumbnail-btn p-0 border-0 bg-transparent"
                      onClick={() => setActiveIndex(index)}
                    >
                      <img 
                        src={image} 
                        alt={`Thumbnail ${index + 1}`}
                        className={`img-fluid rounded ${index === activeIndex ? 'border border-primary border-3' : 'border'}`}
                        style={{ 
                          height: '80px',
                          width: '100%',
                          objectFit: 'cover',
                          opacity: index === activeIndex ? 1 : 0.7,
                          transition: 'opacity 0.3s ease'
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-3">
                <small className="text-muted">
                  Showing {Math.min(activeIndex + 1, carouselImages.length)} of {carouselImages.length} images
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">Our Core Services</h2>
            <p className="text-muted lead">Comprehensive solutions for all your material handling needs</p>
          </div>
          <div className="row g-4">
            {services.slice(0, 4).map(service => (
              <div key={service.id} className="col-md-6 col-lg-3">
                <ServiceCard service={service} />
              </div>
            ))}
          </div>
          <div className="text-center mt-5">
            <Link to="/services" className="btn btn-primary btn-lg">
              View All Services <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-light py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <h2 className="display-5 fw-bold mb-4">Why Choose {companyInfo.name}?</h2>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="bg-white p-4 rounded shadow-sm h-100">
                    <i className="bi bi-shield-check text-primary fs-1 mb-3"></i>
                    <h4>Reliable Service</h4>
                    <p className="text-muted">24/7 support with guaranteed response time</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="bg-white p-4 rounded shadow-sm h-100">
                    <i className="bi bi-award text-primary fs-1 mb-3"></i>
                    <h4>Quality Parts</h4>
                    <p className="text-muted">Genuine spare parts with warranty</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="bg-white p-4 rounded shadow-sm h-100">
                    <i className="bi bi-people text-primary fs-1 mb-3"></i>
                    <h4>Expert Team</h4>
                    <p className="text-muted">Certified technicians with 10+ years experience</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="bg-white p-4 rounded shadow-sm h-100">
                    <i className="bi bi-clock-history text-primary fs-1 mb-3"></i>
                    <h4>Quick Delivery</h4>
                    <p className="text-muted">Same-day service available</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bg-primary text-white p-5 rounded">
                <h3 className="mb-4">Ready to Elevate Your Operations?</h3>
                <p className="mb-4">Contact us today for a free consultation and quote.</p>
                <Link to="/contact" className="btn btn-light btn-lg">
                  Get Free Quote <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;